import axios from "axios";
import { api } from "../config";

// default
// default
const baseUrl = api.API_URL && api.API_URL.endsWith('/') ? api.API_URL.slice(0, -1) : api.API_URL;
axios.defaults.baseURL = baseUrl;
// Avoid hung requests when mobile OS resumes the tab with a stale connection (no default timeout in axios).
axios.defaults.timeout = 60000;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

const LOGIN_PATH_SEGMENT = "api/admin/login";

export function redirectToLogin() {
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const path = `${publicUrl}/login`.replace(/([^:]\/)\/+/g, "$1");
  if (!/\/login\/?$/.test(window.location.pathname)) {
    window.location.replace(path || "/login");
  }
}

/** Clear stored auth and axios header (e.g. expired session, 401). */
export function clearAuthSession() {
  localStorage.removeItem("authUser");
  localStorage.removeItem("expiryAt");
  localStorage.removeItem("expiryIn");
  delete axios.defaults.headers.common["Authorization"];
}

function setAuthorization(explicitToken) {
  if (explicitToken) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${explicitToken}`;
    return;
  }
  const raw = localStorage.getItem("authUser");
  let authUser = null;
  try {
    authUser = raw ? JSON.parse(raw) : null;
  } catch {
    authUser = null;
  }

  if (authUser && authUser.token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${authUser.token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}


// Set authorization on initial load
setAuthorization();

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    // Backend response structure: 
    // Old: { statusCode, status, message, data, ... }
    // New: { error: false/true, msg: "...", data: {...} }
    const responseData = response.data || response;

    // Check for new error format: { error: true, msg: "..." }
    if (responseData.error === true) {
      return Promise.reject(new Error(responseData.msg || responseData.message || "Operation failed"));
    }

    // Check for old error format: { status: 0, message: "..." }
    if (responseData.status === 0) {
      return Promise.reject(new Error(responseData.message || "Operation failed"));
    }

    // Return the full response data (includes error, msg, data or statusCode, status, message, data)
    return responseData;
  },
  function (error) {
    let message;
    if (error.response) {
      // Backend error response structure: { statusCode, message }
      const errorData = error.response.data || {};
      // Support both new `{ error, msg }` and older `{ message }` shapes
      message = errorData.msg || errorData.message || error.message || "An error occurred";

      // Handle specific status codes
      switch (error.response.status) {
        case 500:
          message = errorData.message || "Internal Server Error";
          break;
        case 401: {
          const reqUrl = error.config?.url || "";
          const isLoginAttempt = reqUrl.includes(LOGIN_PATH_SEGMENT);
          message = errorData.msg || errorData.message || "Invalid credentials";
          if (!isLoginAttempt && typeof window !== "undefined") {
            clearAuthSession();
            redirectToLogin();
          }
          break;
        }
        case 404:
          message = errorData.message || "Sorry! the data you are looking for could not be found";
          break;
        case 400:
          message = errorData.msg || errorData.message || "Bad request";
          break;
        default:
          message = errorData.msg || errorData.message || error.message || "An error occurred";
          console.error("API Error Details:", {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            url: error.config?.url,
            method: error.config?.method
          });
      }
      return Promise.reject(new Error(message));
    } else if (error.request) {
      // The request was made but no response was received
      message = "No response received from server";
      return Promise.reject(new Error(message));
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject(new Error(error.message || "An error occurred"));
    }
  }
);

class APIClient {
  /**
   * Fetches data from given url
   */
  get(url, params) {
    setAuthorization(); // Ensure authorization is set before each request

    let response;
    let paramKeys = [];

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          let filterValue = params[key].toString();
          filterValue = filterValue.replace("e=>e.", "");
          paramKeys.push(key + "=" + filterValue);
        }
      });

      const queryString = paramKeys.length ? paramKeys.join("&") : "";
      response = axios.get(`${url}?${queryString}`);
    } else {
      response = axios.get(url);
    }

    return response;
  }
  /**
   * post given data to url
   */
  create(url, data) {
    setAuthorization();

    // If data is FormData, don't set Content-Type header (let browser set it with boundary)
    if (data instanceof FormData) {
      return axios.post(url, data, {
        headers: {
          'Content-Type': undefined // Let browser set the correct multipart/form-data header
        }
      });
    }

    return axios.post(url, data);
  }
  /**
   * Updates data
   */
  update(url, data) {
    setAuthorization();
    return axios.patch(url, data);
  }

  put(url, data) {
    setAuthorization();
    if (data instanceof FormData) {
      return axios.put(url, data, {
        headers: {
          'Content-Type': undefined // Let browser set the correct multipart/form-data header
        }
      });
    }
    return axios.put(url, { ...data });
  }
  /**
   * Delete
   */
  delete(url, config) {
    setAuthorization();
    if (config && typeof config === 'object') {
      return axios.delete(url, { ...config });
    }
    return axios.delete(url);
  }
}

const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  if (!user) {
    return null;
  }
  return JSON.parse(user);
};

export { APIClient, setAuthorization, getLoggedinUser };
